import React, { useState, useEffect } from 'react';
import { request, setSessionToken, wakeServer, waitForServerAwake } from './api';
import ServerWakingScreen from './ServerWakingScreen';

const LoginForm = ({ onLoggedIn, onGoToSignup, onPendingApproval, onForgotPassword }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [waking, setWaking] = useState(false);
  const [wakeStatus, setWakeStatus] = useState('WAKING SERVER...');

  // api.js's session-expiry interceptor stashes this flag + reloads the page whenever a
  // stored token turns out to be stale (e.g. a server-side Redis restart wiped the real
  // session) -- read it once on mount so the login screen actually explains why the user
  // landed back here, instead of looking like an unprompted logout.
  useEffect(() => {
    try {
      if (sessionStorage.getItem('aetheris_session_expired') === '1') {
        sessionStorage.removeItem('aetheris_session_expired');
        setError('Your session expired -- please log in again.');
      }
    } catch (e) {}
  }, []);

  // Fire the wake call right away, before the login request, so the server has
  // as much of a head start as possible. If it's already awake this is a no-op
  // on the Pi side and the health check below resolves on (basically) the first try.
  const handleSubmit = async () => {
    setError(''); setLoading(true);

    wakeServer();

    const alreadyAwake = await waitForServerAwake({
      intervalMs: 1500,
      timeoutMs: 90000,
      onTick: (elapsedMs) => {
        // Only show the splash if this is taking a moment -- a warm server should
        // resolve near-instantly and never show it at all.
        if (elapsedMs > 800) setWaking(true);
        if (elapsedMs > 20000) setWakeStatus('STILL BOOTING CORE...');
        if (elapsedMs > 45000) setWakeStatus('TAKING LONGER THAN USUAL...');
      },
    });
    setWaking(false);

    if (!alreadyAwake) {
      setError('Server is taking too long to wake up. Please try again in a moment.');
      setLoading(false);
      return;
    }

    try {
      const result = await request('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      });
      if (result.status === 'pending_approval') { onPendingApproval && onPendingApproval(); return; }
      setSessionToken(result.session_token);
      onLoggedIn && onLoggedIn(result);
    } catch (e) {
      setError(e.message || 'Login failed');
    } finally { setLoading(false); }
  };

  const handleKey = (e) => { if (e.key === 'Enter') handleSubmit(); };

  if (waking) {
    return <ServerWakingScreen statusText={wakeStatus} />;
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Rajdhani:wght@400;600;700&display=swap');

        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus {
          -webkit-text-fill-color: #e8e0f0 !important;
          -webkit-box-shadow: 0 0 0px 1000px rgba(10,8,20,0.92) inset !important;
          transition: background-color 5000s ease-in-out 0s;
        }



        .login-overlay {
          position: fixed;
          inset: 0;
          background: rgba(5, 4, 15, 0.45);
          z-index: 1;
        }

        .login-root {
          position: relative;
          z-index: 2;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          font-family: 'Rajdhani', sans-serif;
        }

        .login-title {
          font-family: 'Orbitron', monospace;
          font-size: clamp(42px, 8vw, 72px);
          font-weight: 900;
          letter-spacing: 0.25em;
          color: #fff;
          text-shadow:
            0 0 30px rgba(176,38,255,0.5),
            0 0 80px rgba(100,180,255,0.3),
            0 2px 4px rgba(0,0,0,0.8);
          margin-bottom: 32px;
          text-align: center;
        }

        .login-card {
          width: 100%;
          max-width: 460px;
          padding: 0 20px;
        }

        .login-card-inner {
          background: rgba(8, 6, 24, 0.82);
          border: 1.5px solid rgba(176, 38, 255, 0.55);
          border-radius: 4px;
          padding: 32px 28px 28px;
          position: relative;
          backdrop-filter: blur(12px);
          box-shadow:
            0 0 0 1px rgba(100,200,255,0.08),
            0 0 40px rgba(176,38,255,0.15),
            inset 0 0 60px rgba(176,38,255,0.04);
        }

        /* Corner accents like the screenshot */
        .login-card-inner::before,
        .login-card-inner::after {
          content: '';
          position: absolute;
          width: 18px;
          height: 18px;
          border-color: rgba(100,200,255,0.7);
          border-style: solid;
        }
        .login-card-inner::before {
          top: -1px; right: -1px;
          border-width: 2px 2px 0 0;
        }
        .login-card-inner::after {
          bottom: -1px; left: -1px;
          border-width: 0 0 2px 2px;
        }

        .login-gateway-label {
          font-family: 'Orbitron', monospace;
          font-size: 10px;
          letter-spacing: 0.22em;
          color: rgba(100,200,255,0.8);
          margin-bottom: 22px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .login-gateway-label::after {
          content: '';
          flex: 1;
          height: 1px;
          background: linear-gradient(90deg, rgba(100,200,255,0.3), transparent);
        }

        .login-input {
          width: 100%;
          padding: 13px 16px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 6px;
          color: #e8e0f0;
          font-family: 'Rajdhani', sans-serif;
          /* 16px, not 14px: iOS Safari auto-zooms the whole page on focus for any input
             under 16px, which is jarring on the very first screen a phone user hits. Kept
             visually similar in weight via letter-spacing instead of shrinking the type. */
          font-size: 16px;
          font-weight: 600;
          letter-spacing: 0.1em;
          outline: none;
          box-sizing: border-box;
          transition: border-color 0.2s, box-shadow 0.2s;
          margin-bottom: 14px;
        }

        .login-input:focus {
          border-color: rgba(176,38,255,0.7);
          box-shadow: 0 0 12px rgba(176,38,255,0.2);
        }

        .login-input::placeholder { color: rgba(255,255,255,0.35); }

        .login-btn {
          width: 100%;
          padding: 14px;
          background: transparent;
          border: 1.5px solid rgba(255,255,255,0.5);
          border-radius: 6px;
          color: #fff;
          font-family: 'Orbitron', monospace;
          font-size: 12px;
          font-weight: 900;
          letter-spacing: 0.2em;
          cursor: pointer;
          margin-top: 4px;
          transition: background 0.2s, border-color 0.2s, box-shadow 0.2s;
        }

        .login-btn:hover:not(:disabled) {
          background: rgba(176,38,255,0.2);
          border-color: rgba(176,38,255,0.8);
          box-shadow: 0 0 20px rgba(176,38,255,0.3);
        }

        .login-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        .login-links {
          margin-top: 22px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          align-items: center;
        }

        .login-link-btn {
          background: none;
          border: none;
          color: rgba(255,255,255,0.4);
          font-size: 11px;
          font-family: 'Orbitron', monospace;
          letter-spacing: 0.12em;
          cursor: pointer;
          transition: color 0.2s;
          padding: 0;
        }

        .login-link-btn:hover { color: rgba(176,38,255,0.9); }

        .login-error {
          color: #ff4466;
          font-size: 12px;
          font-family: monospace;
          letter-spacing: 0.05em;
          margin-bottom: 10px;
        }
      `}</style>

      {/* Static background */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 0,
        backgroundImage: 'url(/login-bg-burst.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }} />
      <div className="login-overlay" />

      <div className="login-root">
        <div className="login-title">AETHERIS</div>

        <div className="login-card">
          <div className="login-card-inner">
            <div className="login-gateway-label">// AUTH_GATEWAY</div>

            <input
              className="login-input"
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              onKeyDown={handleKey}
              placeholder="USERNAME"
            />
            <input
              className="login-input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={handleKey}
              placeholder="PASSWORD"
            />

            {error && <div className="login-error">⚠ {error}</div>}

            <button className="login-btn" onClick={handleSubmit} disabled={loading}>
              {loading ? '⟫ CONNECTING...' : '» ACCESS CORE'}
            </button>

            <div className="login-links">
              <button className="login-link-btn" onClick={onForgotPassword}>
                LOST YOUR CIPHER? RECOVER ACCESS
              </button>
              <button className="login-link-btn" onClick={onGoToSignup}>
                CREATE AN ACCOUNT →
              </button>
              {/* Native app download -- one APK for Android TV boxes and phones/tablets */}
              <a className="login-link-btn" href="/aetheris.apk" download
                style={{ textDecoration: 'none', display: 'inline-block' }}>
                📱 GET AETHERIS FOR ANDROID (.APK)
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginForm;
